/**
 * ESPELHO de `core/backend/src/modules/job-description/domain/answer-validation.ts` (DEV-258).
 * O Core é a autoridade (revalida no PATCH e no envio); este espelho existe
 * para a tela validar ao sair do campo. Mude os dois juntos — os testes deste
 * diretório repetem os casos do Core.
 */
/**
 * DEV-260/261 — formato das respostas e validação do envio. Mensagens LITERAIS
 * das anotações de Dev Mode do Figma (specs/139-…/figma-annotations.md §1);
 * limites inclusivos nas pontas (research.md D15).
 */
import type { FormDefinition } from './definition';
import {
  isAnyDisability,
  isDisabilityCriterion,
  isQuestionShown,
  normalizeLabel,
  type Answers,
} from './form-rules';

export const MESSAGES = {
  codigoObrigatorio: 'Preencha o código interno para continuar.',
  codigoInvalido: 'Use só letras, números, hífen e barra nesse código.',
  nomeObrigatorio: 'Preencha o nome da vaga para continuar.',
  nomeCurto: 'O nome precisa ter pelo menos 3 caracteres.',
  nomeLongo: 'O nome pode ter até 120 caracteres.',
  posicoesObrigatorio: 'Informe quantas posições essa vaga tem.',
  posicoesMinimo: 'O número de posições precisa ser 1 ou mais.',
  posicoesMaximo:
    'O máximo é 999 posições. Para mais que isso, fale com a pessoa consultora da Eureca.',
  inteiro: 'Use um número inteiro, sem vírgula.',
  metaMinimo: 'A meta precisa ser 1 ou mais. Se não tiver meta, deixe em branco.',
  escolhaOpcao: 'Escolha uma opção para continuar.',
  localObrigatorio: 'Informe pelo menos uma cidade de trabalho.',
  divulgacaoObrigatorio: 'Escolha como o salário aparece no anúncio.',
  valorObrigatorio: 'Informe o valor para continuar.',
  valorMaiorQueZero: 'O valor precisa ser maior que zero.',
  valorFormato: 'Use só números, com vírgula para os centavos. Ex.: 3.000,00',
  valorMaximo: 'Confira esse valor. O máximo aceito é R$ 999.999,99.',
  minimoMenorQueMaximo: 'O valor mínimo precisa ser menor que o máximo.',
  beneficioLongo: 'O nome pode ter até 60 caracteres.',
  itemRepetido: 'Esse item já está na lista.',
  tiposFormacaoObrigatorio: 'Escolha pelo menos um tipo de formação.',
  /** Só alcançável pela API: o seletor de mês/ano da tela não produz valor inválido. */
  dataInvalida: 'Selecione mês e ano.',
  dataMaximaAnterior: 'A data máxima precisa ser posterior à mínima.',
  areaOuCurso: 'Escolha pelo menos uma área ou um curso.',
  competenciaLonga: 'Cada item pode ter até 40 caracteres.',
  itemEmCompetencias:
    'Esse item já está em Competências. Um requisito não pode ser diferencial ao mesmo tempo.',
  idadeInteira: 'Use um número inteiro de anos.',
  idadeIntervalo: 'A idade precisa estar entre 14 e 99 anos.',
  idadeMinimaMaior: 'A idade mínima precisa ser menor que a máxima.',
  tipoDeficienciaObrigatorio: 'Escolha pelo menos um tipo de deficiência.',
  descricaoObrigatorio: 'Preencha a descrição da vaga para continuar.',
  atividadesObrigatorio: 'Preencha as atividades do dia a dia para continuar.',
  observacoesObrigatorio: 'Preencha as observações sobre o perfil para continuar.',
  generico: 'Preencha este campo para continuar.',
  /** Teto de texto livre (Descritivos e páginas adicionais); o campo da tela já limita. */
  textoLongo: 'O texto pode ter até 5.000 caracteres.',
} as const;

/** Teto de qualquer resposta em texto livre. */
export const MAX_TEXT_LENGTH = 5000;

const REQUIRED_MESSAGE: Record<string, string> = {
  codigo_interno_vaga: MESSAGES.codigoObrigatorio,
  nome_vaga: MESSAGES.nomeObrigatorio,
  numero_posicoes: MESSAGES.posicoesObrigatorio,
  tipo_contratacao: MESSAGES.escolhaOpcao,
  modelo_trabalho: MESSAGES.escolhaOpcao,
  turno_trabalho: MESSAGES.escolhaOpcao,
  carga_horaria_semanal: MESSAGES.escolhaOpcao,
  disponibilidade_mudanca: MESSAGES.escolhaOpcao,
  disponibilidade_viagens: MESSAGES.escolhaOpcao,
  local_trabalho: MESSAGES.localObrigatorio,
  tipo_divulgacao_salario: MESSAGES.divulgacaoObrigatorio,
  valor_minimo: MESSAGES.valorObrigatorio,
  valor_maximo: MESSAGES.valorObrigatorio,
  valor_exato: MESSAGES.valorObrigatorio,
  tipos_formacao: MESSAGES.tiposFormacaoObrigatorio,
  aceita_todos_cursos: MESSAGES.escolhaOpcao,
  tipo_deficiencia: MESSAGES.tipoDeficienciaObrigatorio,
  descricao_vaga: MESSAGES.descricaoObrigatorio,
  atividades_dia_a_dia: MESSAGES.atividadesObrigatorio,
  observacoes_perfil: MESSAGES.observacoesObrigatorio,
};

export function requiredMessage(question: string): string {
  return REQUIRED_MESSAGE[question] ?? MESSAGES.generico;
}

export function isEmptyAnswer(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

const CODE_RE = /^[\p{L}\p{N}/-]+$/u;
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const MAX_MONEY = 999_999.99;

function money(v: unknown): string | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return MESSAGES.valorFormato;
  if (Math.abs(v * 100 - Math.round(v * 100)) > 1e-6) return MESSAGES.valorFormato;
  if (v <= 0) return MESSAGES.valorMaiorQueZero;
  if (v > MAX_MONEY) return MESSAGES.valorMaximo;
  return null;
}

function stringItems(v: unknown, max: number, tooLong: string): string | null {
  if (!Array.isArray(v) || v.some((x) => typeof x !== 'string')) return MESSAGES.generico;
  if ((v as string[]).some((x) => x.trim().length > max)) return tooLong;
  const seen = new Set<string>();
  for (const x of v as string[]) {
    const k = normalizeLabel(x);
    if (seen.has(k)) return MESSAGES.itemRepetido;
    seen.add(k);
  }
  return null;
}

/** Formato de UM valor, independente de obrigatoriedade. `null` = ok. */
export function validateAnswerFormat(question: string, value: unknown): string | null {
  if (isEmptyAnswer(value)) return null;
  switch (question) {
    case 'codigo_interno_vaga':
      return typeof value === 'string' && CODE_RE.test(value.trim())
        ? null
        : MESSAGES.codigoInvalido;
    case 'nome_vaga': {
      if (typeof value !== 'string') return MESSAGES.generico;
      const len = value.trim().length;
      if (len < 3) return MESSAGES.nomeCurto;
      if (len > 120) return MESSAGES.nomeLongo;
      return null;
    }
    case 'numero_posicoes':
      if (typeof value !== 'number' || !Number.isInteger(value)) return MESSAGES.inteiro;
      if (value < 1) return MESSAGES.posicoesMinimo;
      if (value > 999) return MESSAGES.posicoesMaximo;
      return null;
    case 'meta_inscritos':
      if (typeof value !== 'number' || !Number.isInteger(value)) return MESSAGES.inteiro;
      return value < 1 ? MESSAGES.metaMinimo : null;
    case 'valor_minimo':
    case 'valor_maximo':
    case 'valor_exato':
      return money(value);
    case 'beneficios':
    case 'pagamentos_adicionais':
      return stringItems(value, 60, MESSAGES.beneficioLongo);
    case 'competencias':
    case 'diferenciais':
      return stringItems(value, 40, MESSAGES.competenciaLonga);
    case 'data_minima_formacao':
    case 'data_maxima_formacao':
      return typeof value === 'string' && MONTH_RE.test(value) ? null : MESSAGES.dataInvalida;
    case 'aceita_todos_cursos':
      return typeof value === 'boolean' ? null : MESSAGES.escolhaOpcao;
    case 'idade_minima':
    case 'idade_maxima':
      if (typeof value !== 'number' || !Number.isInteger(value)) return MESSAGES.idadeInteira;
      return isAgeInRange(value) ? null : MESSAGES.idadeIntervalo;
    default:
      return typeof value === 'string' && value.length > MAX_TEXT_LENGTH
        ? MESSAGES.textoLongo
        : null;
  }
}

/** DEV-260 AC-49 — idade inteira de 14 a 99, inclusive. */
function isAgeInRange(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 14 && v <= 99;
}

export interface AnswerIssue {
  page: string;
  question: string;
  reason: 'required' | 'invalid';
  message: string;
}

function choicesOf(def: FormDefinition, question: string): string[] | null {
  for (const p of def.pages) {
    if (p.kind !== 'standard') continue;
    const q = p.questions.find((x) => x.name === question);
    if (q) return q.choices;
  }
  return null;
}

/** Regras entre campos de uma página (mínimo×máximo, datas, competências×diferenciais, "Qualquer uma"). */
export function crossFieldIssues(answers: Answers): { question: string; message: string }[] {
  const out: { question: string; message: string }[] = [];
  const min = answers.valor_minimo;
  const max = answers.valor_maximo;
  if (
    isQuestionShown('valor_minimo', answers) &&
    typeof min === 'number' &&
    typeof max === 'number' &&
    min >= max
  ) {
    out.push({ question: 'valor_minimo', message: MESSAGES.minimoMenorQueMaximo });
  }
  const dmin = answers.data_minima_formacao;
  const dmax = answers.data_maxima_formacao;
  if (
    typeof dmin === 'string' &&
    typeof dmax === 'string' &&
    MONTH_RE.test(dmin) &&
    MONTH_RE.test(dmax) &&
    dmax < dmin
  ) {
    out.push({ question: 'data_maxima_formacao', message: MESSAGES.dataMaximaAnterior });
  }
  // AC-48: só o intervalo invertido (iguais passam); fora de 14–99 já é erro de formato.
  const amin = answers.idade_minima;
  const amax = answers.idade_maxima;
  if (isAgeInRange(amin) && isAgeInRange(amax) && amin > amax) {
    out.push({ question: 'idade_minima', message: MESSAGES.idadeMinimaMaior });
  }
  const skills = Array.isArray(answers.competencias) ? (answers.competencias as unknown[]) : [];
  const diffs = Array.isArray(answers.diferenciais) ? (answers.diferenciais as unknown[]) : [];
  const skillKeys = new Set(
    skills.filter((s): s is string => typeof s === 'string').map(normalizeLabel)
  );
  if (diffs.some((d) => typeof d === 'string' && skillKeys.has(normalizeLabel(d)))) {
    out.push({ question: 'diferenciais', message: MESSAGES.itemEmCompetencias });
  }
  const pcd = Array.isArray(answers.tipo_deficiencia)
    ? (answers.tipo_deficiencia as unknown[])
    : [];
  if (pcd.some((x) => typeof x === 'string' && isAnyDisability(x)) && pcd.length > 1) {
    out.push({ question: 'tipo_deficiencia', message: MESSAGES.escolhaOpcao });
  }
  return out;
}

/** Exigência do tipo escolhido; rótulo casado sem caixa e acento (como as `choices`). */
export function requiredFor(def: FormDefinition, answers: Answers): Set<string> {
  const label = typeof answers.tipo_contratacao === 'string' ? answers.tipo_contratacao : null;
  const key =
    label === null
      ? undefined
      : Object.keys(def.requiredByContractType).find(
          (k) => normalizeLabel(k) === normalizeLabel(label)
        );
  const list =
    (key !== undefined ? def.requiredByContractType[key] : undefined) ?? def.requiredDefault;
  return new Set(list);
}

/**
 * Validação completa do envio contra a projeção e o tipo corrente. Respostas de
 * perguntas escondidas (configuração ou condicional) são ignoradas, não erros.
 */
export function validateSubmission(def: FormDefinition, answers: Answers): AnswerIssue[] {
  const required = requiredFor(def, answers);
  const issues: AnswerIssue[] = [];

  for (const page of def.pages) {
    if (page.kind === 'custom') {
      for (const q of page.requiredQuestionNames) {
        if (isEmptyAnswer(answers[q])) {
          issues.push({
            page: page.name,
            question: q,
            reason: 'required',
            message: MESSAGES.generico,
          });
        }
      }
      continue;
    }
    for (const q of page.questions) {
      if (!isQuestionShown(q.name, answers)) continue;
      const value = answers[q.name];
      if (isEmptyAnswer(value)) {
        // "Pelo menos uma área ou curso" (AC-46): exigência de GRUPO, em `cursos`.
        if (q.name === 'areas_curso') continue;
        if (q.name === 'cursos') {
          if (required.has('aceita_todos_cursos') && isEmptyAnswer(answers.areas_curso)) {
            issues.push({
              page: page.name,
              question: 'cursos',
              reason: 'required',
              message: MESSAGES.areaOuCurso,
            });
          }
          continue;
        }
        if (required.has(q.name)) {
          issues.push({
            page: page.name,
            question: q.name,
            reason: 'required',
            message: requiredMessage(q.name),
          });
        }
        continue;
      }
      const format = validateAnswerFormat(q.name, value);
      if (format) {
        issues.push({ page: page.name, question: q.name, reason: 'invalid', message: format });
        continue;
      }
      const choices = q.choices;
      if (choices) {
        const values = Array.isArray(value) ? value : [value];
        const allowed = new Set(choices.map(normalizeLabel));
        if (values.some((v) => typeof v !== 'string' || !allowed.has(normalizeLabel(v)))) {
          issues.push({
            page: page.name,
            question: q.name,
            reason: 'invalid',
            message: MESSAGES.escolhaOpcao,
          });
        }
      }
    }
  }

  const pageOf = (question: string) =>
    def.pages.find((p) =>
      p.kind === 'standard' ? p.questions.some((q) => q.name === question) : false
    )?.name;
  for (const c of crossFieldIssues(answers)) {
    const page = pageOf(c.question);
    if (page && !issues.some((i) => i.question === c.question)) {
      issues.push({ page, question: c.question, reason: 'invalid', message: c.message });
    }
  }
  return issues;
}

export { choicesOf, isDisabilityCriterion };
